// ============================================================================
// Componente: FinanceiroNumerarioConciliacao.js
// Objetivo: a conferência semanal do dinheiro — as três pernas, loja a loja.
//
// ⚠️ A TELA MOSTRA DUAS COMPARAÇÕES SEPARADAS, DE PROPÓSITO.
//   Elas respondem perguntas diferentes e por isso ficam em blocos visuais
//   distintos, com cabeçalho de grupo, em vez de virarem uma linha só:
//
//     · CAIXA    (Realizado × Declarado, na semana quarta→terça)
//                 → a loja declarou o que vendeu em dinheiro?
//     · CONTAGEM (Declarado × Apurado, envelope a envelope, sem janela)
//                 → a Prosegur contou o que a loja declarou?
//
//   Fundir as duas produziria um número que não responde pergunta nenhuma —
//   a guia não é uma janela de calendário (há envelope com 43 dias de
//   defasagem) e a quarta-feira é partida entre duas guias. O porquê completo
//   está no cabeçalho do server/financeiro/numerario-conciliacao.js.
//
// ⚠️ NADA AQUI ACUSA NINGUÉM. Diferença é ponto de partida de investigação.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
    Scale, AlertTriangle, Loader2, CheckCircle2, Info, CalendarDays,
} from 'lucide-react';
import {
    COR, COR_CLARA, COR_BORDA, COR_NEUTRA,
    dinheiro, dataBR, diaSemana, mesDe, rotuloMes, rotuloSemana, parFiltro,
    percentual, corDaDiferenca, estiloSituacao, th, td, tdNum,
} from './numerarioFormato';

// Por que uma loja pode não ter guia na semana. Espelha o registro do backend
// em `server/financeiro/prosegur-lojas.js`, que é a fonte da verdade.
//
// ⚠️ Cada loja aqui tem relatório PRÓPRIO da Prosegur (cliente à parte), em dia
//    que pode não ser o da quarta das demais — então a ausência numa semana
//    significa "o relatório DESTA loja ainda não foi importado", não "não
//    depositou" e nem R$ 0,00. É diferente de uma das lojas da quarta sumir, que
//    é achado (relatório incompleto na origem).
//
// ⚠️ MAL e MOX entraram em 2026-08-17; o dia do MOX é TERÇA (o "segunda" que
//    ficou escrito por dez dias era suposição, desmentida pelo arquivo real).
// ⚠️ PCI e PCS entraram em 2026-10-13: ATÉ ali eram "sem coleta — numerário no
//    escritório", mas a Prosegur passou a recolhê-las (clientes 36326 e 36323),
//    com relatório próprio. Medido na primeira coleta (30/09/2026, uma quarta) —
//    não cravamos o dia da semana com uma amostra só, daí o texto genérico.
const MOTIVO_SEM_GUIA = {
    MAL: 'coleta na sexta — relatório desta semana ainda não importado',
    MOX: 'coleta na terça — relatório desta semana ainda não importado',
    PCI: 'relatório próprio (cliente Prosegur) — desta semana ainda não importado',
    PCS: 'relatório próprio (cliente Prosegur) — desta semana ainda não importado',
};

/** "qua 08/07/2026 · 01/07 a 07/07 · MAL" — o rótulo de uma coleta no filtro. */
const rotuloColeta = (c) => {
    const periodo = `${dataBR(c.janela.inicio).slice(0, 5)} a ${dataBR(c.janela.fim).slice(0, 5)}`;
    const quem = c.lojas.length <= 2 && c.lojas.length > 0
        ? c.lojas.join(', ')
        : `${c.lojas.length} lojas`;
    return `${diaSemana(c.dtColeta)} ${dataBR(c.dtColeta)} · ${periodo} · ${quem}`;
};


const FinanceiroNumerarioConciliacao = () => {
    const [semanas, setSemanas] = useState([]);
    // Mês (AAAA-MM) que recorta a lista de semanas. A cada relatório importado o
    // seletor de semanas cresce; sem este filtro ele vira uma lista infinita.
    const [mes, setMes] = useState('');
    const [selecionada, setSelecionada] = useState('');
    // A coleta em foco. ⚠️ SEMPRE tem uma — não existe "todas". A conferência é
    // feita malote a malote, e misturar coletas numa tela só faz cada bloco de
    // números se referir a um período diferente sem que a tela possa dizer qual.
    const [coletaFiltro, setColetaFiltro] = useState('');
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState(null);

    const token = () => localStorage.getItem('token') || localStorage.getItem('authToken');

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch('/api/numerario/semanas', {
                    headers: { Authorization: `Bearer ${token()}` },
                });
                if (!res.ok) return;
                const lista = await res.json();
                setSemanas(lista);
                // Abre no mês e na semana mais recentes (a lista já vem em ordem
                // decrescente) — é a semana que o Financeiro acabou de importar.
                if (lista.length) {
                    setMes(mesDe(lista[0].semana));
                    setSelecionada(String(lista[0].semana).slice(0, 10));
                }
            } catch {
                setErro('Não foi possível carregar as semanas disponíveis.');
            }
        })();
    }, []);

    const carregar = useCallback(async (dt) => {
        if (!dt) return;
        setCarregando(true);
        setErro(null);
        try {
            const res = await fetch(`/api/numerario/conciliacao/${dt}`, {
                headers: { Authorization: `Bearer ${token()}` },
            });
            const json = await res.json();
            if (!res.ok) { setErro(json.erro || 'Falha ao calcular a conferência.'); setDados(null); return; }
            setDados(json);
        } catch {
            setErro('Não foi possível falar com o servidor.');
            setDados(null);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(selecionada); }, [selecionada, carregar]);

    // As semanas do mês escolhido — é esta lista que alimenta o 2º seletor.
    const semanasDoMes = semanas.filter((s) => mesDe(s.semana) === mes);
    const meses = [...new Set(semanas.map((s) => mesDe(s.semana)))];

    // Trocar de mês leva para a semana mais recente dele; sem isso a semana
    // selecionada continuaria a de outro mês e os dois filtros se contradiriam.
    useEffect(() => {
        if (!mes) return;
        const doMes = semanas.filter((s) => mesDe(s.semana) === mes);
        if (doMes.length && !doMes.some((s) => String(s.semana).slice(0, 10) === selecionada)) {
            setSelecionada(String(doMes[0].semana).slice(0, 10));
        }
    }, [mes, semanas, selecionada]);

    const coletas = dados?.coletas || [];
    const todas = dados?.semana || [];
    // As coletas que valem escolher: a âncora sem guia nenhuma não é um malote —
    // mas ela precisa entrar mesmo assim SE tiver linhas, senão as lojas sem
    // coleta (PCI, PCS) ficariam inalcançáveis por qualquer filtro.
    const coletasEscolhiveis = coletas.filter(
        (c) => c.lojas.length > 0 || todas.some((l) => l.dtColeta === c.dtColeta),
    );

    // Trocar de semana reposiciona a coleta em foco. O padrão é a coleta com
    // MAIS lojas — a da quarta, que é o grosso da rede e o que o Financeiro abre
    // para conferir. Sem isso, cair na coleta de uma loja só pareceria tela vazia.
    useEffect(() => {
        if (!dados) return;
        const cs = (dados.coletas || []).filter(
            (c) => c.lojas.length > 0 || (dados.semana || []).some((l) => l.dtColeta === c.dtColeta),
        );
        if (!cs.length) { setColetaFiltro(''); return; }
        if (!cs.some((c) => c.dtColeta === coletaFiltro)) {
            const maior = cs.reduce((a, b) => (b.lojas.length > a.lojas.length ? b : a), cs[0]);
            setColetaFiltro(maior.dtColeta);
        }
    }, [dados, coletaFiltro]);

    const linhas = coletaFiltro ? todas.filter((l) => l.dtColeta === coletaFiltro) : todas;
    const coletaAtual = coletas.find((c) => c.dtColeta === coletaFiltro) || null;
    // ⚠️ Como há SEMPRE uma coleta em foco, a janela exibida é sempre a dela — e
    //   por isso a frase "venda considerada de X a Y" voltou a ser sempre
    //   verdadeira. Ela só era falsa quando a tela misturava duas janelas.
    const janelaUnica = coletaAtual ? coletaAtual.janela : (dados?.janela || null);
    const resumo = {
        exatas: linhas.filter((l) => l.situacao === 'Exato').length,
        comDiferenca: linhas.filter((l) => l.situacao === 'Sobra' || l.situacao === 'Falta').length,
        incompletas: linhas.filter((l) => l.situacao === 'Declarado incompleto').length,
    };

    return (
        <div style={{ padding: 24, maxWidth: 1280, margin: '0 auto' }}>
            <header style={{ marginBottom: 18 }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, color: COR, margin: 0 }}>
                    <Scale size={22} /> Conferência do numerário
                </h2>
                <p style={{ color: '#475569', marginTop: 6, fontSize: 14, maxWidth: 860 }}>
                    Duas perguntas diferentes, lado a lado: a loja <strong>declarou</strong> o que vendeu
                    em dinheiro, e a Prosegur <strong>contou</strong> o que a loja declarou.
                </p>
            </header>

            {/* ── Seletor de semana ─────────────────────────────────────── */}
            <section style={{
                background: COR_CLARA, border: `1px solid ${COR_BORDA}`, borderRadius: 12,
                padding: 16, marginBottom: 20, display: 'flex', gap: 14,
                alignItems: 'center', flexWrap: 'wrap',
            }}>
                {/* 1º filtro: MÊS. A lista de semanas cresce a cada relatório
                    importado — sem este recorte ela vira uma lista sem fim. */}
                <span style={parFiltro}>
                    <label htmlFor="mes" style={{
                        display: 'inline-flex', alignItems: 'center', gap: 8,
                        fontWeight: 600, color: COR, fontSize: 14,
                    }}>
                        <CalendarDays size={17} /> Período
                    </label>
                    <select
                        id="mes"
                        value={mes}
                        onChange={(e) => setMes(e.target.value)}
                        style={{
                            padding: '8px 12px', borderRadius: 8, border: `1px solid ${COR_BORDA}`,
                            fontSize: 14, background: '#fff', color: '#0f172a', minWidth: 140,
                        }}
                    >
                        {meses.length === 0 && <option value="">—</option>}
                        {meses.map((m) => <option key={m} value={m}>{rotuloMes(m)}</option>)}
                    </select>
                </span>

                {/* 2º filtro: SEMANA (dentro do mês). */}
                <span style={parFiltro}>
                    <label htmlFor="semana" style={{ fontWeight: 600, color: COR, fontSize: 14 }}>
                        Semana
                    </label>
                    <select
                        id="semana"
                        value={selecionada}
                        onChange={(e) => setSelecionada(e.target.value)}
                        style={{
                            padding: '8px 12px', borderRadius: 8, border: `1px solid ${COR_BORDA}`,
                            fontSize: 14, background: '#fff', color: '#0f172a', maxWidth: 460,
                        }}
                    >
                        {semanasDoMes.length === 0 && <option value="">Nenhum relatório importado</option>}
                        {semanasDoMes.map((s) => {
                            const d = String(s.semana).slice(0, 10);
                            return <option key={d} value={d}>{rotuloSemana(s)}</option>;
                        })}
                    </select>
                </span>

                {/* 3º filtro: COLETA. ⚠️ SEM opção "todas", de propósito: a
                    conferência é malote a malote, e cada coleta tem o seu período
                    de venda. Uma tela com duas coletas juntas teria dois períodos
                    e não poderia anunciar nenhum — foi o que confundiu na 1ª
                    versão. Aqui sempre há uma coleta em foco, e o período ao lado
                    é sempre o dela. */}
                {coletasEscolhiveis.length > 1 && (
                    <span style={parFiltro}>
                        <label htmlFor="coleta-filtro" style={{ fontWeight: 600, color: COR, fontSize: 14 }}>
                            Coleta
                        </label>
                        <select
                            id="coleta-filtro"
                            value={coletaFiltro}
                            onChange={(e) => setColetaFiltro(e.target.value)}
                            style={{
                                padding: '8px 12px', borderRadius: 8, border: `1px solid ${COR_BORDA}`,
                                fontSize: 14, background: '#fff', color: '#0f172a', minWidth: 250,
                            }}
                        >
                            {coletasEscolhiveis.map((c) => (
                                <option key={c.dtColeta} value={c.dtColeta}>{rotuloColeta(c)}</option>
                            ))}
                        </select>
                    </span>
                )}

                {dados && janelaUnica && (
                    <span style={{ color: '#475569', fontSize: 14 }}>
                        Venda considerada de <strong>{dataBR(janelaUnica.inicio)}</strong> a{' '}
                        <strong>{dataBR(janelaUnica.fim)}</strong>
                    </span>
                )}
                {carregando && <Loader2 size={18} className="animate-spin" color={COR} />}
            </section>

            {erro && (
                <div style={aviso('#fef2f2', '#fecaca', '#991b1b')}>
                    <AlertTriangle size={18} /> <span>{erro}</span>
                </div>
            )}

            {/* ── Resumo honesto ─────────────────────────────────────────── */}
            {dados && linhas.length > 0 && (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                    <Pilula cor="#15803d" fundo="#f0fdf4" texto={`${resumo.exatas} loja(s) fecharam exato`} />
                    {resumo.comDiferenca > 0 && (
                        <Pilula cor="#1d4ed8" fundo="#eff6ff" texto={`${resumo.comDiferenca} com sobra ou falta`} />
                    )}
                    {resumo.incompletas > 0 && (
                        <Pilula cor="#b45309" fundo="#fffbeb"
                            texto={`${resumo.incompletas} sem conferir — carga do Flex incompleta`} />
                    )}
                </div>
            )}

            {/* ── As coletas da semana ───────────────────────────────────────
                Só aparece quando a semana tem mais de uma. ⚠️ Não é enfeite: sem
                este bloco, ver MAL medida de 03 a 09/07 na mesma tela em que as
                outras vão de 01 a 07/07 parece erro de sistema. A rede recolhe
                em três dias (quarta, sexta e terça) e cada malote cobre os SEUS
                sete dias. */}
            {dados && coletas.length > 1 && (
                <div style={{
                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
                    padding: '12px 16px', marginBottom: 16, fontSize: 13.5, color: '#334155',
                }}>
                    <div style={{ fontWeight: 600, color: COR, marginBottom: 6 }}>
                        Esta semana teve {coletas.length} coletas — cada uma cobre os seus 7 dias
                    </div>
                    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                        {coletas.map((c) => {
                            // A coleta filtrada fica em destaque; as demais, apagadas.
                            // Sem isso, com o filtro ativo a tela mostra uma lista de
                            // períodos e uma tabela que só tem um deles.
                            const ativa = !coletaFiltro || c.dtColeta === coletaFiltro;
                            return (
                                <span key={c.dtColeta} style={{ opacity: ativa ? 1 : 0.4 }}>
                                    <strong>{diaSemana(c.dtColeta)} {dataBR(c.dtColeta)}</strong>{' '}
                                    <span style={{ color: COR_NEUTRA }}>
                                        ({dataBR(c.janela.inicio)}–{dataBR(c.janela.fim)})
                                    </span>{' '}
                                    {c.lojas.length ? c.lojas.join(', ') : <em style={{ color: COR_NEUTRA }}>lojas sem coleta</em>}
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── A tabela ───────────────────────────────────────────────── */}
            {dados && linhas.length > 0 && (
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                        <thead>
                            {/* Cabeçalho de GRUPO — é ele que impede a leitura de que
                                as duas diferenças são a mesma coisa. */}
                            <tr>
                                <th style={{ ...th, borderBottom: '1px solid #e2e8f0' }} />
                                <th colSpan={5} style={grupo('#0d7a74')}>
                                    Caixa — a loja declarou o que vendeu?
                                </th>
                                <th colSpan={4} style={grupo('#6d28d9')}>
                                    Depósito — o que foi vendido chegou ao banco?
                                </th>
                            </tr>
                            <tr>
                                <th style={th}>Loja</th>
                                <th style={{ ...th, textAlign: 'right' }}>Vendido</th>
                                <th style={{ ...th, textAlign: 'right' }}>Declarado</th>
                                <th style={{ ...th, textAlign: 'right', color: COR_NEUTRA }}
                                    title="Troco e câmbio: dinheiro que entrou no caixa sem ser venda. Não entra na conferência — aparece porque é ele que explica um depósito maior que a venda.">
                                    Troco/câmbio
                                </th>
                                <th style={{ ...th, textAlign: 'right' }}>Diferença</th>
                                <th style={th}>Situação</th>
                                <th style={{ ...th, textAlign: 'right', borderLeft: '1px solid #e2e8f0' }}
                                    title="O que a Prosegur contou e levou ao banco.">Apurado</th>
                                <th style={{ ...th, textAlign: 'right', color: COR_NEUTRA }}
                                    title="Depositado direto no BANESTES, fora do malote da Prosegur (marcado como BANESTES no campo do envelope). Soma no depósito porque o apurado da Prosegur não o contém.">
                                    Banestes
                                </th>
                                <th style={{ ...th, textAlign: 'right' }}
                                    title="(Apurado + Banestes) menos o vendido. Leia junto do período coberto pela guia: o vendido é da semana, mas o malote pode carregar envelope atrasado.">
                                    Dif. depósito mapa
                                </th>
                                <th style={{ ...th, textAlign: 'right' }}>Cobertura</th>
                            </tr>
                        </thead>
                        <tbody>
                            {linhas.map((l) => {
                                const est = estiloSituacao(l.situacao);
                                const g = l.guia;
                                return (
                                    <tr key={l.loja}>
                                        {/* Sem selo de coleta na linha: como o filtro
                                            garante uma coleta por tela, TODAS as linhas
                                            são do mesmo malote e do mesmo período — que
                                            está anunciado uma vez, lá em cima. */}
                                        <td style={td}><strong>{l.loja}</strong></td>
                                        <td style={tdNum}>{dinheiro(l.realizado)}</td>
                                        <td style={tdNum}>{dinheiro(l.declarado)}</td>
                                        {/* Fora da conferência de propósito (não é venda), mas
                                            visível: sem ele, um depósito maior que a venda fica
                                            sem explicação na tela. */}
                                        <td style={{ ...tdNum, color: COR_NEUTRA }}>
                                            {l.naoVenda ? dinheiro(l.naoVenda) : '—'}
                                        </td>
                                        <td style={{ ...tdNum, color: corDaDiferenca(l.diferenca) }}>
                                            {l.diferenca === null && l.diferencaBruta !== undefined
                                                // Mostra o número bruto entre parênteses: ele não é
                                                // confiável, mas esconder atrapalha quem investiga.
                                                ? <span style={{ color: COR_NEUTRA }} title="Valor bruto — não confiável, o declarado está incompleto">
                                                    ({dinheiro(l.diferencaBruta)})
                                                </span>
                                                : dinheiro(l.diferenca)}
                                        </td>
                                        <td style={td}>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                                background: est.fundo, color: est.cor,
                                                padding: '3px 9px', borderRadius: 20,
                                                fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
                                            }}
                                                title={l.avisos?.[0] || ''}
                                            >
                                                {l.situacao === 'Exato' && <CheckCircle2 size={13} />}
                                                {l.situacao === 'Declarado incompleto' && <AlertTriangle size={13} />}
                                                {l.situacao === 'Sem venda na Sovos' && <Info size={13} />}
                                                {l.situacao}
                                            </span>
                                        </td>

                                        {g ? (
                                            <>
                                                <td style={{ ...tdNum, borderLeft: '1px solid #e2e8f0' }}
                                                    title={`Diferença de contagem (apurado − declarado): ${dinheiro(g.diferenca)}`}>
                                                    {dinheiro(g.vlApurado)}
                                                </td>
                                                {/* Fora do malote da Prosegur — por isso soma
                                                    do lado do depósito. Só a GTV usa hoje. */}
                                                <td style={{ ...tdNum, color: COR_NEUTRA }}>
                                                    {l.banestes ? dinheiro(l.banestes) : '—'}
                                                </td>
                                                <td style={{ ...tdNum, color: corDaDiferenca(l.deposito?.diferenca) }}
                                                    title={periodoDaGuia(g, dados.janela)}>
                                                    {dinheiro(l.deposito?.diferenca)}
                                                    {/* ⚠️ O malote pode carregar envelope de semanas
                                                        anteriores. Sem esta marca, o FER de 08/07
                                                        aparece com +85% e ninguém descobre que a
                                                        guia cobria 15 dias, não 7. */}
                                                    {periodoEstourado(g, dados.janela) && (
                                                        <AlertTriangle size={13} color="#b45309"
                                                            style={{ marginLeft: 5, verticalAlign: '-2px' }} />
                                                    )}
                                                </td>
                                                <td style={{
                                                    ...tdNum,
                                                    color: g.confiavel ? COR_NEUTRA : '#b45309',
                                                    fontWeight: g.confiavel ? 400 : 700,
                                                }}
                                                    title={g.avisos?.[0] || tituloCobertura(g)}
                                                >
                                                    {percentual(g.cobertura)}
                                                </td>
                                            </>
                                        ) : (
                                            // ⚠️ Ausência de relatório NÃO é depósito zero — em nenhum dos
                                            //    dois casos. Para as quatro lojas conhecidas a tela diz
                                            //    POR QUE não há guia; para as demais, a ausência é o
                                            //    próprio achado (o relatório da semana pode estar
                                            //    incompleto — ver modo de falha #4 do README).
                                            <td colSpan={4} style={{
                                                ...td, borderLeft: '1px solid #e2e8f0',
                                                color: '#94a3b8', fontStyle: 'italic', fontSize: 13,
                                            }}>
                                                {MOTIVO_SEM_GUIA[l.loja] || 'sem relatório da Prosegur'}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Avisos detalhados ──────────────────────────────────────── */}
            {dados && (
                <Avisos linhas={linhas} />
            )}

            {dados && linhas.length === 0 && !carregando && (
                <p style={{ color: '#94a3b8', fontSize: 14 }}>
                    Nenhum dado para esta semana.
                </p>
            )}
        </div>
    );
};

// ── Peças ───────────────────────────────────────────────────────────────────

const Avisos = ({ linhas }) => {
    const todos = [
        ...linhas.flatMap((l) => (l.avisos || []).map((a) => ({ loja: l.loja, texto: a }))),
        ...linhas.flatMap((l) => (l.guia?.avisos || []).map((a) => ({ loja: l.loja, texto: a }))),
    ];
    if (!todos.length) return null;
    return (
        <section style={{ marginTop: 20 }}>
            <h3 style={{ fontSize: 14, color: '#b45309', display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={16} /> Por que algumas linhas não puderam ser conferidas
            </h3>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20, color: '#475569', fontSize: 13.5, lineHeight: 1.7 }}>
                {todos.map((a, i) => (
                    <li key={i}><strong>{a.loja}</strong> — {a.texto}</li>
                ))}
            </ul>
        </section>
    );
};

/**
 * A guia cobre um período maior que a semana da venda?
 * Quando cobre, a diferença de depósito compara períodos diferentes — e o
 * número engana quem não souber disso.
 */
const periodoEstourado = (g, janela) => {
    const p = g?.periodoCoberto;
    if (!p || !janela) return false;
    return p.inicio < janela.inicio;
};

/**
 * O texto do "quantos envelopes têm lastro".
 *
 * O lastro pode vir de DUAS fontes: o Flex (o caixa registrou) e, como fallback,
 * o módulo Troco (a loja não lançou no PDV, mas o portal tem a banana). Quando a
 * segunda entrou em jogo, o número precisa dizer isso — senão o Financeiro vai
 * procurar no Flex um envelope que nunca esteve lá.
 */
const tituloCobertura = (g) => {
    const base = `${g.envelopesCasados} de ${g.envelopes} envelopes com lastro encontrado`;
    return g.envelopesCasadosPorTroco
        ? `${base} (${g.envelopesCasadosPorTroco} pelo módulo Troco — o PDV da loja não registrou)`
        : `${base} no Flex`;
};

const periodoDaGuia = (g, janela) => {
    const p = g?.periodoCoberto;
    if (!p) return 'Sem envelopes casados no Flex — período da guia desconhecido.';
    const base = `Esta guia recolheu envelopes de ${dataBR(p.inicio)} a ${dataBR(p.fim)} (${p.dias} dias).`;
    return periodoEstourado(g, janela)
        ? `${base}
Começa ANTES da semana da venda (${dataBR(janela.inicio)}) — o malote carrega envelope atrasado, então esta diferença compara períodos diferentes.`
        : base;
};

const Pilula = ({ cor, fundo, texto }) => (
    <span style={{
        background: fundo, color: cor, border: `1px solid ${cor}22`,
        padding: '6px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600,
    }}>{texto}</span>
);

const grupo = (cor) => ({
    ...th, textAlign: 'center', color: cor, fontSize: 11.5,
    borderBottom: '1px solid #e2e8f0', background: `${cor}0d`,
});

const aviso = (fundo, borda, cor) => ({
    display: 'flex', gap: 10, alignItems: 'center',
    background: fundo, border: `1px solid ${borda}`, color: cor,
    borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14,
});

export default FinanceiroNumerarioConciliacao;
